import { OpenSearchQueryBuilder, type FilterClause } from 'tio-common/utils/open-search-builder';
import {
  buildApprovalShouldClauses,
  type PendingStatusType,
} from 'tio-common/utils/tuition-assistance/approval-queue-filters';

/** Inputs for the approval queue search; mirrors the queue page's query params. */
export interface ApprovalQueueSearchOptions {
  /** Current company; an absent id searches company -1, which matches nothing. */
  companyId: string | null | undefined;
  /** The viewer's employee id at `companyId`; reviewer filters compare against it. */
  reviewerId: string;
  /** Pending-status filter; `tas_approvable` is treated as `all` because tio-employee can't show assets. */
  approval?: PendingStatusType | '' | null;
  /** Program template id to narrow to. */
  program?: string | null;
  /** A named sort (`last_action_asc`, `first_submitted_asc`, …) or a raw sort string. */
  order?: string;
  page?: number;
  limit?: number;
  /** Skip the reviewer filter entirely (admins browsing every pending item). */
  disableReviewer?: boolean;
  /** Items the viewer could review but that aren't their turn yet. */
  waitingOnOthers?: boolean;
  /** Free-text search across the queue documents. */
  search?: string;
}

const DEFAULT_SORT =
  'tas_application_latest_state_change_at_date.asc,tas_program_instance_latest_state_change_at_date.asc,tas_asset_updated_at_date.asc';

/** Maps the queue's named sort options to OpenSearch sort strings; anything else passes through. */
export function resolveApprovalQueueSort(order: string | undefined): string {
  switch (order) {
    case 'last_action_asc':
      return DEFAULT_SORT;
    case 'last_action_desc':
      return 'tas_application_latest_state_change_at_date.desc,tas_program_instance_latest_state_change_at_date.desc,tas_asset_updated_at_date.desc';
    case 'first_submitted_asc':
      return 'attention_priority_date';
    case 'first_submitted_desc':
      return '-attention_priority_date';
    default:
      // Backward compatibility: support old or explicit field-based values from URLs.
      return order || DEFAULT_SORT;
  }
}

/**
 * The approval queue's search request: pending applications and program
 * instances for the company, filtered to the viewer's turn (or, with
 * `waitingOnOthers`, to items they could review but that wait on someone
 * else). The queue page and the partner dashboard both run it, so their
 * counts agree.
 *
 * @returns the body for `ApprovalQueueRefreshService.search` / `executeSearchRequest`
 */
export function buildApprovalQueueSearch(options: ApprovalQueueSearchOptions) {
  const { companyId, reviewerId, approval, program, order, search } = options;
  const { page = 1, limit = 25, disableReviewer, waitingOnOthers } = options;

  const builder = new OpenSearchQueryBuilder()
    .addFilter({ term: { company_id: companyId || -1 } })
    .addFilterIf(!disableReviewer && !waitingOnOthers, {
      bool: {
        should: [
          { term: { tas_program_instance_current_reviewers_keyword: reviewerId } },
          { term: { tas_application_current_reviewers_keyword: reviewerId } },
          { term: { tas_approvable_current_reviewers_keyword: reviewerId } },
        ],
        minimum_should_match: 1,
      },
    })
    .addFilterIf(Boolean(waitingOnOthers), {
      bool: {
        must: [
          // Must be in possible_reviewers (I could potentially review this)
          {
            bool: {
              should: [
                { term: { tas_program_instance_possible_reviewers_keyword: reviewerId } },
                { term: { tas_application_possible_reviewers_keyword: reviewerId } },
                { term: { tas_approvable_possible_reviewers_keyword: reviewerId } },
              ],
              minimum_should_match: 1,
            },
          },
          // Must NOT be in current_reviewers (not my turn right now)
          {
            bool: {
              must_not: [
                { term: { tas_program_instance_current_reviewers_keyword: reviewerId } },
                { term: { tas_application_current_reviewers_keyword: reviewerId } },
                { term: { tas_approvable_current_reviewers_keyword: reviewerId } },
              ],
            },
          },
        ],
      },
    })
    .addFilterIf(Boolean(program), { term: { tas_program_template_id_keyword: program ?? '' } })
    .setSort(resolveApprovalQueueSort(order), {
      defaultMissing: '_last',
      defaultUnmappedType: 'date',
    })
    .setPagination(page, limit);

  const effectiveApproval = approval === 'tas_approvable' ? 'all' : approval || 'all';
  const shouldClauses = buildApprovalShouldClauses(effectiveApproval, {
    includeTasApprovable: false,
  });
  shouldClauses.forEach((clause: FilterClause) => builder.addShould(clause));
  if (shouldClauses.length > 0) {
    builder.setMinimumShouldMatch(1);
  }

  if (search) {
    builder.setSearchTerm(search, ['*'], { fuzziness: 'AUTO' });
  }

  return {
    'model-type': 'TasProgramInstance',
    'search-body': builder.build(),
    ...(search && { 'search-term': search }),
  };
}
